import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  TabPanel,
  TabPanels,
  Tabs
} from "@chakra-ui/react"
import { AugmentationPanel } from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import {
  AugmentationToolbarOption,
  useAugmentationToolbar
} from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import useAugmentationPanel from "@pathwright/ux/src/components/augmentation/useAugmentationPanel"
import React from "react"
import { useToggleIntercomUI } from "../../intercom/IntercomProvider"
import { useCertificateContext } from "../context/CertificateContext"
import { useCertificateState } from "../context/CertificateState"
import CertificateAugmentationHeader from "./CertificateAugmentationHeader"
import CertificateSubmitButton from "./CertificateSubmitButton"
import DesignPanel from "./DesignPanel"
import VariablesPanel from "./VariablesPanel"

export function useCertificateAugmentationToolbar() {
  const options: AugmentationToolbarOption[] = [
    {
      key: "design",
      icon: "edit",
      label: "Design",
      "aria-label": "Certificate design settings",
      AugmentationComponent: DesignPanel
    },
    {
      key: "variables",
      icon: "test-tube",
      label: "Variables",
      "aria-label": "Certificate variables settings",
      AugmentationComponent: VariablesPanel
    }
  ]

  return useAugmentationToolbar(options)
}

const CertificateAugmentation: AugmentationPanel = React.forwardRef(
  ({ toolbar, disclosure, gutter, offset, allowOverlay }, ref) => {
    // Hide Intercom UI when drawer is open.
    useToggleIntercomUI(disclosure?.isOpen)

    const { getDrawerProps, getDrawerContentProps } = useAugmentationPanel({
      disclosure,
      offset: { ...offset, top: 50 },
      allowOverlay,
      gutter,
      size: "xs"
    })

    const tabIndex = toolbar.options.findIndex(
      (option) => option?.key === toolbar.selectedKey
    )

    const { certificate } = useCertificateContext()
    const {
      certificateStoredState: { discardCount }
    } = useCertificateState()
    const showSubmit = Boolean(certificate || discardCount)

    return (
      <Tabs
        variant="line"
        colorScheme="gray"
        index={tabIndex}
        isLazy
        lazyBehavior="keepMounted"
      >
        <Drawer {...getDrawerProps()}>
          <DrawerContent ref={ref} {...getDrawerContentProps()}>
            <DrawerHeader>
              <CertificateAugmentationHeader
                toolbar={toolbar}
                disclosure={disclosure}
              />
            </DrawerHeader>
            <DrawerBody>
              <TabPanels>
                {toolbar.options.map((option) =>
                  option.AugmentationComponent ? (
                    <TabPanel key={option.key} p={0}>
                      <option.AugmentationComponent toolbar={toolbar} />
                    </TabPanel>
                  ) : null
                )}
              </TabPanels>
            </DrawerBody>
            {showSubmit && (
              <DrawerFooter justifyContent="center">
                <CertificateSubmitButton />
              </DrawerFooter>
            )}
          </DrawerContent>
        </Drawer>
      </Tabs>
    )
  }
)

export default CertificateAugmentation
